import React, { useState, useEffect } from 'react';
import { useLanguage, useAuth, api } from '../context/AppContext';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { 
  Flame, 
  Loader2, 
  Trophy,
  Crown,
  Star,
  Zap,
  CheckCircle2
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';

const StreakDisplay = ({ compact = false }) => {
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loadingLeaderboard, setLoadingLeaderboard] = useState(false);

  useEffect(() => {
    if (user) {
      fetchStreak();
    }
  }, [user]);

  const fetchStreak = async () => {
    try {
      const response = await api.get('/streaks/me');
      setStreak(response.data);
    } catch (error) {
      console.error('Error fetching streak:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    setLoadingLeaderboard(true);
    try {
      const response = await api.get('/streaks/leaderboard');
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error('Error fetching leaderboard:', error);
    } finally {
      setLoadingLeaderboard(false);
    }
  };

  const handleOpenDetails = () => {
    setShowDetails(true);
    fetchLeaderboard();
  };

  const getFlameColor = (days) => {
    if (days >= 30) return 'text-yellow-400';
    if (days >= 14) return 'text-orange-400';
    if (days >= 7) return 'text-orange-500';
    return 'text-orange-600';
  };

  const getFlameSize = (days) => {
    if (days >= 30) return 'w-10 h-10';
    if (days >= 14) return 'w-9 h-9';
    if (days >= 7) return 'w-8 h-8';
    return 'w-7 h-7';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <Loader2 className="w-5 h-5 text-primary animate-spin" />
      </div>
    );
  }

  if (!streak) return null;

  // Compact version for dashboard header
  if (compact) {
    return (
      <button
        onClick={handleOpenDetails}
        className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-orange-500/20 to-yellow-500/20 rounded-lg hover:from-orange-500/30 hover:to-yellow-500/30 transition-all"
        data-testid="streak-display-compact"
      >
        <motion.div
          animate={{ 
            scale: streak.is_active_today ? [1, 1.1, 1] : 1,
          }}
          transition={{ 
            duration: 0.5, 
            repeat: streak.is_active_today ? Infinity : 0,
            repeatDelay: 2
          }}
        >
          <Flame className={`${getFlameSize(streak.current_streak)} ${getFlameColor(streak.current_streak)}`} />
        </motion.div>
        <span className="font-heading text-xl text-foreground">{streak.current_streak}</span>
        {!streak.is_active_today && streak.current_streak > 0 && (
          <span className="text-xs text-orange-400 animate-pulse">!</span>
        )}
      </button>
    );
  }

  // Full card version
  return (
    <>
      <Card 
        className="bg-gradient-to-br from-orange-500/10 to-yellow-500/10 border-orange-500/30 cursor-pointer hover:border-orange-500/50 transition-colors"
        onClick={handleOpenDetails}
        data-testid="streak-display"
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <motion.div
                animate={{ 
                  scale: streak.is_active_today ? [1, 1.15, 1] : 1,
                  rotate: streak.is_active_today ? [0, -5, 5, 0] : 0
                }}
                transition={{ 
                  duration: 0.6, 
                  repeat: streak.is_active_today ? Infinity : 0,
                  repeatDelay: 2
                }}
                className="relative"
              >
                <Flame className={`${getFlameSize(streak.current_streak)} ${getFlameColor(streak.current_streak)} drop-shadow-lg`} />
                {streak.current_streak >= 7 && (
                  <motion.div
                    className="absolute -top-1 -right-1"
                    animate={{ scale: [1, 1.2, 1] }}
                    transition={{ duration: 1, repeat: Infinity }}
                  >
                    <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                  </motion.div>
                )}
              </motion.div>
              
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider">
                  {language === 'fr' ? 'Série actuelle' : 'Current Streak'}
                </p>
                <p className="font-heading text-3xl text-foreground">
                  {streak.current_streak} <span className="text-lg text-muted-foreground">{language === 'fr' ? 'jours' : 'days'}</span>
                </p>
              </div>
            </div>
            
            <div className="text-right">
              <p className="text-xs text-muted-foreground">
                {language === 'fr' ? 'Record' : 'Best'}
              </p>
              <p className="font-heading text-xl text-yellow-400">
                {streak.longest_streak}
              </p>
            </div>
          </div>
          
          {!streak.is_active_today && streak.current_streak > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 p-2 bg-orange-500/20 rounded-lg text-center"
            >
              <p className="text-sm text-orange-400">
                {language === 'fr' 
                  ? "⚡ Log un repas aujourd'hui pour garder ta série!"
                  : "⚡ Log a meal today to keep your streak!"}
              </p>
            </motion.div>
          )}
          
          {streak.is_active_today && (
            <div className="mt-3 flex items-center gap-2 text-green-400 text-sm">
              <CheckCircle2 className="w-4 h-4" />
              {language === 'fr' ? 'Repas logué aujourd\'hui!' : 'Meal logged today!'}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Details Dialog */}
      <Dialog open={showDetails} onOpenChange={setShowDetails}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
              <Flame className="w-6 h-6 text-orange-500" />
              {language === 'fr' ? 'Mes Streaks' : 'My Streaks'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Current Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gradient-to-br from-orange-500/20 to-yellow-500/20 p-4 rounded-lg text-center">
                <Flame className={`${getFlameSize(streak.current_streak)} ${getFlameColor(streak.current_streak)} mx-auto mb-2`} />
                <p className="font-heading text-3xl text-foreground">{streak.current_streak}</p>
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Série actuelle' : 'Current streak'}
                </p>
              </div>
              <div className="bg-secondary/50 p-4 rounded-lg text-center">
                <Trophy className="w-8 h-8 text-yellow-400 mx-auto mb-2" />
                <p className="font-heading text-3xl text-foreground">{streak.longest_streak}</p>
                <p className="text-xs text-muted-foreground">
                  {language === 'fr' ? 'Meilleure série' : 'Best streak'}
                </p>
              </div>
            </div>

            {/* Milestones */}
            <div>
              <h4 className="font-heading text-sm uppercase text-primary mb-3">
                {language === 'fr' ? 'Jalons' : 'Milestones'}
              </h4>
              <div className="grid grid-cols-4 gap-2">
                {streak.milestones?.map((milestone, idx) => (
                  <div
                    key={idx}
                    className={`p-2 rounded-lg text-center transition-all ${
                      milestone.achieved 
                        ? 'bg-primary/20 border border-primary/30' 
                        : 'bg-secondary/30 opacity-50'
                    }`}
                  >
                    <span className="text-2xl">{milestone.icon}</span>
                    <p className="text-xs text-muted-foreground mt-1">{milestone.days}j</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Leaderboard */}
            <div>
              <h4 className="font-heading text-sm uppercase text-primary mb-3 flex items-center gap-2">
                <Crown className="w-4 h-4" />
                {language === 'fr' ? 'Classement' : 'Leaderboard'}
              </h4>
              
              {loadingLeaderboard ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-5 h-5 text-primary animate-spin" />
                </div>
              ) : leaderboard.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {language === 'fr' ? 'Aucun classement disponible' : 'No leaderboard available'}
                </p>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {leaderboard.slice(0, 10).map((entry, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-2 rounded-lg ${
                        entry.is_me ? 'bg-primary/20 border border-primary/30' : 'bg-secondary/30'
                      }`}
                    >
                      <span className={`font-heading text-lg w-6 ${
                        idx === 0 ? 'text-yellow-400' : idx === 1 ? 'text-gray-400' : idx === 2 ? 'text-orange-400' : 'text-muted-foreground'
                      }`}>
                        {idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm truncate ${entry.is_me ? 'text-primary font-medium' : 'text-foreground'}`}>
                          {entry.name} {entry.is_me && '(toi)'}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Flame className="w-4 h-4 text-orange-500" />
                        <span className="font-heading text-foreground">{entry.current_streak}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default StreakDisplay;
